(() => {
  'use strict';

  const API_ENDPOINT =
    'https://api.bazaarvoice.com/data/reviews.json';

  const API_VERSION = '5.4';

  const TOTAL_FETCH = 100;

  function escapeHTML(value) {
    const element = document.createElement('div');

    element.textContent =
      value == null ? '' : String(value);

    return element.innerHTML;
  }

  function formatDate(dateString) {
    if (!dateString) {
      return '';
    }

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  }

  function getPhotoUrl(review) {
    if (!review || !Array.isArray(review.Photos)) {
      return '';
    }

    for (const photo of review.Photos) {
      if (!photo || !photo.Sizes) {
        continue;
      }

      const image =
        photo.Sizes.large ||
        photo.Sizes.normal ||
        photo.Sizes.thumbnail;

      if (image && image.Url) {
        return image.Url;
      }
    }

    return '';
  }

  function hasCustomerPhoto(review) {
    return Boolean(getPhotoUrl(review));
  }

  function renderStars(rating) {
    const value = Math.max(
      0,
      Math.min(
        5,
        Number(rating) || 0
      )
    );

    return `
      <div
        class="bv-homepage-reviews__rating"
        aria-label="${value} out of 5 stars"
      >
        ${Array.from(
          { length: 5 },
          (_, index) => `
            <span
              class="bv-homepage-reviews__star${index < value ? ' is-active' : ''}"
              aria-hidden="true"
            >★</span>
          `
        ).join('')}
      </div>
    `;
  }

  function renderReview(review) {
    const imageUrl = getPhotoUrl(review);

    if (!imageUrl) {
      return '';
    }

    const title = escapeHTML(review.Title);

    const text = escapeHTML(review.ReviewText);

    const author = escapeHTML(
      review.UserNickname || 'Verified Customer'
    );

    const product = escapeHTML(
      review.OriginalProductName || ''
    );

    const date = formatDate(
      review.SubmissionTime
    );

    return `
      <article class="bv-homepage-reviews__card">

        <div class="bv-homepage-reviews__photo">
          <img
            src="${escapeHTML(imageUrl)}"
            alt="Customer review photo"
            loading="lazy"
            width="600"
            height="400"
          >
        </div>

        <div class="bv-homepage-reviews__content">

          ${renderStars(review.Rating)}

          <div class="bv-homepage-reviews__meta">

            <span class="bv-homepage-reviews__author">
              ${author}
            </span>

            <span class="bv-homepage-reviews__verified">
              Verified
            </span>

            ${
              date
                ? `
                  <time
                    class="bv-homepage-reviews__date"
                    datetime="${escapeHTML(review.SubmissionTime)}"
                  >
                    ${date}
                  </time>
                `
                : ''
            }

          </div>

          ${
            title
              ? `
                <h3 class="bv-homepage-reviews__title">
                  ${title}
                </h3>
              `
              : ''
          }

          ${
            text
              ? `
                <div class="bv-homepage-reviews__text">
                  ${text}
                </div>
              `
              : ''
          }

          ${
            product
              ? `
                <div class="bv-homepage-reviews__product">
                  ${product}
                </div>
              `
              : ''
          }

        </div>

      </article>
    `;
  }

  function initSection(section) {
    if (
      section.dataset.bvInitialized === 'true'
    ) {
      return;
    }

    section.dataset.bvInitialized = 'true';

    const apiKey =
      section.dataset.bvApiKey;

    const reviewList =
      section.querySelector(
        '[data-bv-review-list]'
      );

    const carousel =
      section.querySelector(
        '.bv-homepage-reviews__carousel'
      );

    const prevButton =
      section.querySelector(
        '[data-bv-prev]'
      );

    const nextButton =
      section.querySelector(
        '[data-bv-next]'
      );

    const dotsContainer =
      section.querySelector(
        '[data-bv-dots]'
      );

    const loader =
      section.querySelector(
        '[data-bv-loader]'
      );

    if (!apiKey) {
      console.error(
        'Bazaarvoice Reviews: API key is missing.'
      );

      return;
    }

    if (!reviewList || !carousel) {
      console.error(
        'Bazaarvoice Reviews: Required carousel elements are missing.'
      );

      return;
    }

    /*
     * IMPORTANT ADA FIX
     *
     * Do NOT assign:
     * role="group"
     * role="tabpanel"
     *
     * to the pagination/dots container.
     *
     * The carousel semantics are already defined
     * on .bv-homepage-reviews__carousel-wrapper
     * in the Liquid markup.
     */

    let currentSlide = 0;

    let resizeTimer = null;

    let touchStartX = 0;

    let touchEndX = 0;

    function getCardsPerView() {
      const width = window.innerWidth;

      if (width <= 749) {
        return 1;
      }

      if (width <= 1199) {
        return 2;
      }

      return 4;
    }

    function getGap() {
      const styles =
        window.getComputedStyle(
          reviewList
        );

      const gap = parseFloat(
        styles.columnGap ||
        styles.gap
      );

      return Number.isFinite(gap)
        ? gap
        : 22;
    }

    function getMaxSlide() {
      const cardCount =
        reviewList.children.length;

      const cardsPerView =
        getCardsPerView();

      return Math.max(
        0,
        cardCount - cardsPerView
      );
    }

    function updateButtons() {
      const maxSlide =
        getMaxSlide();

      if (prevButton) {
        prevButton.disabled =
          currentSlide <= 0;
      }

      if (nextButton) {
        nextButton.disabled =
          currentSlide >= maxSlide;
      }
    }

    function updateDots() {
      if (!dotsContainer) {
        return;
      }

      const cardCount =
        reviewList.children.length;

      const cardsPerView =
        getCardsPerView();

      if (
        cardCount === 0 ||
        cardCount <= cardsPerView
      ) {
        dotsContainer.hidden = true;

        dotsContainer.innerHTML = '';

        return;
      }

      const pageCount =
        Math.ceil(
          cardCount / cardsPerView
        );

      dotsContainer.hidden = false;

      dotsContainer.innerHTML = '';

      const activePage =
        Math.floor(
          currentSlide / cardsPerView
        );

      for (
        let index = 0;
        index < pageCount;
        index++
      ) {
        const dot =
          document.createElement(
            'button'
          );

        dot.type = 'button';

        dot.className =
          'bv-homepage-reviews__dot';

        /*
         * Native button + accessible name
         */
        dot.setAttribute(
          'aria-label',
          `Go to review page ${index + 1}`
        );

        /*
         * Indicates the current pagination item
         */
        dot.setAttribute(
          'aria-current',
          index === activePage
            ? 'true'
            : 'false'
        );

        if (index === activePage) {
          dot.classList.add(
            'is-active'
          );
        }

        dot.addEventListener(
          'click',
          () => {
            currentSlide =
              Math.min(
                index * cardsPerView,
                getMaxSlide()
              );

            updateCarousel();
          }
        );

        dotsContainer.appendChild(
          dot
        );
      }
    }

    function updateCarousel() {
      const cards =
        reviewList.children;

      if (!cards.length) {
        return;
      }

      currentSlide =
        Math.max(
          0,
          Math.min(
            currentSlide,
            getMaxSlide()
          )
        );

      const firstCard =
        cards[0];

      if (!firstCard) {
        return;
      }

      const cardWidth =
        firstCard.getBoundingClientRect()
          .width;

      const gap =
        getGap();

      const translateAmount =
        (cardWidth + gap) *
        currentSlide;

      reviewList.style.transform =
        `translate3d(-${translateAmount}px, 0, 0)`;

      updateButtons();

      updateDots();
    }

    function showEmptyState(message) {
      reviewList.innerHTML = `
        <div class="bv-homepage-reviews__error">
          ${escapeHTML(message)}
        </div>
      `;

      reviewList.style.transform =
        'none';

      if (prevButton) {
        prevButton.disabled = true;
      }

      if (nextButton) {
        nextButton.disabled = true;
      }

      if (dotsContainer) {
        dotsContainer.hidden = true;

        dotsContainer.innerHTML = '';
      }
    }

    async function loadReviews() {
      if (loader) {
        loader.hidden = false;
      }

      if (prevButton) {
        prevButton.disabled = true;
      }

      if (nextButton) {
        nextButton.disabled = true;
      }

      reviewList.innerHTML = '';

      reviewList.style.transform =
        'none';

      const params =
        new URLSearchParams();

      params.set(
        'apiversion',
        API_VERSION
      );

      params.set(
        'passkey',
        apiKey
      );

      params.set(
        'Limit',
        String(TOTAL_FETCH)
      );

      params.set(
        'Offset',
        '0'
      );

      params.set(
        'Sort',
        'SubmissionTime:desc'
      );

      params.set(
        'Filter',
        'Rating:gte:3'
      );

      try {
        const requestUrl =
          `${API_ENDPOINT}?${params.toString()}`;

        const response =
          await fetch(
            requestUrl,
            {
              method: 'GET',
              headers: {
                Accept:
                  'application/json'
              }
            }
          );

        if (!response.ok) {
          throw new Error(
            `Bazaarvoice API returned ${response.status}`
          );
        }

        const data =
          await response.json();

        if (data.HasErrors) {
          throw new Error(
            'Bazaarvoice API returned errors.'
          );
        }

        const allReviews =
          Array.isArray(data.Results)
            ? data.Results
            : [];

        const photoReviews =
          allReviews.filter(
            hasCustomerPhoto
          );

        if (!photoReviews.length) {
          showEmptyState(
            'No customer photo reviews are currently available.'
          );

          return;
        }

        const reviewHTML =
          photoReviews
            .map(renderReview)
            .filter(Boolean)
            .join('');

        if (!reviewHTML) {
          showEmptyState(
            'No customer photo reviews are currently available.'
          );

          return;
        }

        reviewList.innerHTML =
          reviewHTML;

        currentSlide = 0;

        requestAnimationFrame(() => {
          updateCarousel();
        });

      } catch (error) {
        console.error(
          'Bazaarvoice Reviews Error:',
          error
        );

        showEmptyState(
          'Reviews are currently unavailable.'
        );

      } finally {
        if (loader) {
          loader.hidden = true;
        }
      }
    }

    if (nextButton) {
      nextButton.addEventListener(
        'click',
        () => {
          currentSlide =
            Math.min(
              currentSlide + 1,
              getMaxSlide()
            );

          updateCarousel();
        }
      );
    }

    if (prevButton) {
      prevButton.addEventListener(
        'click',
        () => {
          currentSlide =
            Math.max(
              currentSlide - 1,
              0
            );

          updateCarousel();
        }
      );
    }

    /*
     * Touch / Swipe
     */
    reviewList.addEventListener(
      'touchstart',
      event => {
        if (
          !event.changedTouches ||
          !event.changedTouches.length
        ) {
          return;
        }

        touchStartX =
          event.changedTouches[0]
            .clientX;
      },
      {
        passive: true
      }
    );

    reviewList.addEventListener(
      'touchend',
      event => {
        if (
          !event.changedTouches ||
          !event.changedTouches.length
        ) {
          return;
        }

        touchEndX =
          event.changedTouches[0]
            .clientX;

        const difference =
          touchStartX - touchEndX;

        if (
          Math.abs(difference) < 50
        ) {
          return;
        }

        if (difference > 0) {
          currentSlide =
            Math.min(
              currentSlide + 1,
              getMaxSlide()
            );
        } else {
          currentSlide =
            Math.max(
              currentSlide - 1,
              0
            );
        }

        updateCarousel();
      },
      {
        passive: true
      }
    );

    /*
     * Responsive recalculation
     */
    window.addEventListener(
      'resize',
      () => {
        clearTimeout(
          resizeTimer
        );

        resizeTimer =
          setTimeout(() => {
            currentSlide = 0;

            requestAnimationFrame(
              () => {
                updateCarousel();
              }
            );
          },
          200
        );
      }
    );

    loadReviews();
  }

  function init() {
    document
      .querySelectorAll(
        '[data-bv-reviews]'
      )
      .forEach(
        initSection
      );
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );
  } else {
    init();
  }
})();